import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Label } from './ui/label';
import api from '../utils/api';
import { 
  LayoutDashboard,
  Euro,
  Clock,
  Globe,
  Home,
  Building,
  TrendingUp,
  Filter,
  Wallet,
  Search
} from 'lucide-react';

// Import KPI sub-components
import KPIPanoramica from './kpi/KPIPanoramica';
import KPIOccupazione from './kpi/KPIOccupazione';
import KPIFinanziaria from './kpi/KPIFinanziaria';
import KPIFinanziariAvanzati from './kpi/KPIFinanziariAvanzati';
import KPICashFlow from './kpi/KPICashFlow';
import KPIAIInsights from './kpi/KPIAIInsights';
import KPITemporale from './kpi/KPITemporale';
import KPIPiattaforme from './kpi/KPIPiattaforme';
import KPIProprieta from './kpi/KPIProprieta';

const KPIAdmin = () => {
  const [bookings, setBookings] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('panoramica');
  
  // Filtri globali
  const [filters, setFilters] = useState({
    // Filtri temporali
    period: 'year',
    year: new Date().getFullYear(),
    month: '',
    quarter: '',
    dateFrom: '',
    dateTo: '',
    
    // Filtri geografici (per futuro)
    country: '',
    region: '',
    city: '',
    
    // Filtri immobile
    propertyId: '',
    propertySearch: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bookingsRes, propertiesRes] = await Promise.all([
        api.get('/bookings'),
        api.get('/properties')
      ]);
      setBookings(bookingsRes.data);
      setProperties(propertiesRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'panoramica', label: 'Panoramica', icon: LayoutDashboard, component: KPIPanoramica },
    { id: 'occupazione', label: 'Occupazione', icon: Building, component: KPIOccupazione },
    { id: 'finanziaria', label: 'Analisi Finanziaria', icon: Euro, component: KPIFinanziaria },
    { id: 'finanziari-avanzati', label: 'Finanziari Avanzati', icon: TrendingUp, component: KPIFinanziariAvanzati },
    { id: 'cash-flow', label: '💰 Cash Flow & Break-Even', icon: Wallet, component: KPICashFlow },
    { id: 'ai-insights', label: '🤖 AI Insights', icon: TrendingUp, component: KPIAIInsights },
    { id: 'temporale', label: 'Analisi Temporale', icon: Clock, component: KPITemporale },
    { id: 'piattaforme', label: 'Piattaforme', icon: Globe, component: KPIPiattaforme },
    { id: 'proprieta', label: 'Proprietà', icon: Building, component: KPIProprieta }
  ];

  const ActiveComponent = tabs.find(t => t.id === activeTab)?.component || KPIPanoramica;

  if (loading) {
    return (
      <Layout title="KPI Dashboard">
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="KPI Dashboard">
      <div className="space-y-6">
        {/* Global Filters */}
        <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <div className="flex items-center space-x-2 mb-4">
            <Filter className="h-5 w-5 text-blue-900" />
            <h3 className="text-lg font-bold text-blue-900">Filtri Globali</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            {/* Year */}
            <div>
              <Label className="text-xs text-gray-600 mb-1 block">Anno</Label>
              <Select value={filters.year.toString()} onValueChange={(value) => setFilters({...filters, year: parseInt(value)})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2025">2025</SelectItem>
                  <SelectItem value="2024">2024</SelectItem>
                  <SelectItem value="2023">2023</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Period */}
            <div>
              <Label className="text-xs text-gray-600 mb-1 block">Periodo</Label>
              <Select value={filters.period} onValueChange={(value) => setFilters({...filters, period: value, month: '', quarter: ''})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="year">Anno</SelectItem>
                  <SelectItem value="quarter">Trimestre</SelectItem>
                  <SelectItem value="month">Mese</SelectItem>
                  <SelectItem value="custom">Personalizzato</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Month (conditional) */}
            {filters.period === 'month' && (
              <div>
                <Label className="text-xs text-gray-600 mb-1 block">Mese</Label>
                <Select value={filters.month} onValueChange={(value) => setFilters({...filters, month: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona mese" />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(12)].map((_, i) => (
                      <SelectItem key={i + 1} value={(i + 1).toString()}>
                        {new Date(2025, i).toLocaleDateString('it-IT', { month: 'long' })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Quarter (conditional) */}
            {filters.period === 'quarter' && (
              <div>
                <Label className="text-xs text-gray-600 mb-1 block">Trimestre</Label>
                <Select value={filters.quarter} onValueChange={(value) => setFilters({...filters, quarter: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona trimestre" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Q1 (Gen-Mar)</SelectItem>
                    <SelectItem value="2">Q2 (Apr-Giu)</SelectItem>
                    <SelectItem value="3">Q3 (Lug-Set)</SelectItem>
                    <SelectItem value="4">Q4 (Ott-Dic)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Property Filter */}
            <div>
              <Label className="text-xs text-gray-600 mb-1 block">Immobile</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
                <Input
                  placeholder="Cerca immobile..."
                  value={filters.propertySearch || ""}
                  onChange={(e) => setFilters({...filters, propertySearch: e.target.value, propertyId: ""})}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Geographic Filters */}
            <div>
              <Label className="text-xs text-gray-600 mb-1 block">Paese</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
                <Input
                  placeholder="Cerca paese..."
                  value={filters.country || ""}
                  onChange={(e) => setFilters({...filters, country: e.target.value})}
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs text-gray-600 mb-1 block">Regione</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
                <Input
                  placeholder="Cerca regione..."
                  value={filters.region || ""}
                  onChange={(e) => setFilters({...filters, region: e.target.value})}
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs text-gray-600 mb-1 block">Provincia</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
                <Input
                  placeholder="Cerca provincia..."
                  value={filters.province || ""}
                  onChange={(e) => setFilters({...filters, province: e.target.value})}
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs text-gray-600 mb-1 block">Città</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
                <Input
                  placeholder="Cerca città..."
                  value={filters.city || ""}
                  onChange={(e) => setFilters({...filters, city: e.target.value})}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Reset Button */}
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => setFilters({
                  period: 'year',
                  year: new Date().getFullYear(),
                  month: '',
                  quarter: '',
                  dateFrom: '',
                  dateTo: '',
                  country: '',
                  region: '',
                  province: '',
                  city: '',
                  propertyId: '',
                  propertySearch: ''
                })}
                className="w-full"
              >
                Reset
              </Button>
            </div>
          </div>
        </Card>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
          <div className="flex border-b border-gray-200 overflow-x-auto">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center space-x-2 px-6 py-4 font-semibold transition-all whitespace-nowrap
                    ${activeTab === tab.id 
                      ? 'bg-blue-900 text-white border-b-4 border-blue-600' 
                      : 'text-gray-600 hover:bg-gray-50 hover:text-blue-900'
                    }
                  `}
                >
                  <Icon className="h-5 w-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Active Tab Content */}
          <div className="p-6">
            <ActiveComponent 
              bookings={bookings} 
              properties={properties}
              filters={filters}
            />
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default KPIAdmin;
